import {
  Box,
  BoxProps,
  CheckBox,
  ColorOptions,
  Icon,
  IconNew,
  Image,
  Loader,
  MaterialTextInput,
  ModBox,
  Spacer,
  Text,
  TextNew,
  TextProps,
} from '@jfs/ui-new'
import { useNavigation } from '@react-navigation/native'
import { FC, useCallback, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { Dimensions, FlatList, KeyboardAvoidingView } from 'react-native'
import * as Yup from 'yup'
import { VerifiedIcon } from '../../assets/images/svg/Verified_Icon'
import { Modal } from '../../comps/Modal'
import { ModalConfirmDelete } from '../../comps/PaymentModes/ModalConfirmDelete'
import { PaySecurelyButton } from '../../comps/PaySecurelyButton'
import { ToastMessage } from '../../comps/ToastMessageV2'
import { UPIType } from '../../data/models/UPI'
import { useLazyApiData, useStores } from '../../data/store'
import {
  allVpa as allVpaUtils,
  analyticsEvent,
  convertHexToRGBA,
  isIFrame,
  MessageTypes,
  sendErrorIfNotIframe,
} from '../../utils'
import { ADD_NEW_UPI, MAX_RETRIES_EXCEEDED_ERROR, pages, PAYMENT_MODE_SUBTYPES } from '../../utils/constants'
import { staticStrings } from '../../utils/string'
import { vpaOrNumber } from '../../utils/validations'
import { UPIToolTip } from './UPIToolTip'

interface UPIInputModalProps {
  show: boolean
  isDirectUPI?: boolean
  onDismiss: () => void
}

const { height } = Dimensions.get('window')

const allVpa: any = (isSubscriptionEnabled: boolean) => allVpaUtils

const popularVpa: any = (isSubscriptionEnabled: boolean) => [
  '@jio',
  '@ybl',
  '@paytm',
  '@okaxis',
  '@okhdfcbank',
  '@okicici',
  '@oksbi',
  '@upi',
]

const UPIInputModal: FC<UPIInputModalProps> = (props: UPIInputModalProps) => {
  const { show, isDirectUPI = false, onDismiss } = props

  const { theme, vpaList, closeModal, updateCancelModal, updateModalDisplaying, isUpiIntentAllowed } = useStores(
    (root) => ({
      theme: root?.config?.merchantTheme,
      vpaList: root.getVpaList(),
      closeModal: root.config.closeModal,
      updateCancelModal: root.config.updateCancelModal,
      updateModalDisplaying: root.config.updateModalDisplaying,
      isUpiIntentAllowed: root.merchant.isUpiIntentAllowed,
    }),
  )

  const [addNew, setAddNew] = useState(false)

  useEffect(() => {
    if (closeModal && show) {
      updateModalDisplaying(false)
      updateCancelModal(false)
      onDismiss()
    }
  }, [closeModal, onDismiss, show, updateCancelModal, updateModalDisplaying])

  const onModalDismiss = () => {
    if ((vpaList.length > 0 && !addNew) || vpaList.length === 0) {
      onDismiss()
    } else if (vpaList.length > 0 && addNew) {
      setAddNew(false)
    }
  }

  if (!show && isUpiIntentAllowed) {
    return null
  }

  return (
    <ModelWrapper
      vpaList={vpaList}
      addNew={addNew}
      onModalDismiss={onModalDismiss}
      theme={theme}
      isDirectUPI={isDirectUPI}
      isUpiIntentAllowed={isUpiIntentAllowed}>
      {vpaList.length > 0 && !addNew && (
        <SavedVPA onModalDismiss={onModalDismiss} addNew={() => setAddNew(!addNew)} isDirectUPI={isDirectUPI} />
      )}
      {(vpaList.length === 0 || addNew) && <AddNew onModalDismiss={onModalDismiss} isDirectUPI={isDirectUPI} />}
    </ModelWrapper>
  )
}

const ModelWrapper = ({ children, vpaList, addNew, onModalDismiss, theme, isDirectUPI, isUpiIntentAllowed }: any) => {
  if (isUpiIntentAllowed && !isDirectUPI) {
    return (
      <Modal
        type={vpaList.length === 0 || addNew ? 'Bottom_Flex_Grow' : 'BottomTop'}
        show={true}
        isFullWidth={true}
        onDismiss={onModalDismiss}
        title={'Add New VPA'}
        theme={theme}>
        {children}
      </Modal>
    )
  } else {
    return (
      <Box position="absolute" height={'100%'} backgroundColor={theme.bodyBgColor as ColorOptions} width="100%">
        <Box overflowY="scroll" flex={1}>
          {children}
        </Box>
      </Box>
    )
  }
}

const BankNameItem = ({ theme, item, onPress }: { theme: any; item: any; onPress: any }) => {
  const handlePress = () => onPress(item)

  const style = styles(theme)
  return (
    <Box {...style.item} onPress={handlePress} underlayColor="GRAY_LIGHT">
      <>
        <Text {...style.tagText}>{item?.toLowerCase()}</Text>
      </>
    </Box>
  )
}

const SavedVPA = ({
  onModalDismiss,
  addNew,
  isDirectUPI,
}: {
  onModalDismiss: () => void
  addNew: () => void
  isDirectUPI: boolean
}) => {
  const {
    theme,
    vpaList,
    netAmount,
    returnUrl,
    validateVpaApi,
    setMessage,
    confirmPayment,
    offerApplied,
    validateOffer,
    applyOffer,
    removeOffer,
    isSubscriptionEnabled,
    isUpiIntentAllowed,
    cancelIntent,
    redirectUrl,
    intentId,
    deleteCardApi,
  } = useStores((root) => ({
    theme: root.config.merchantTheme,
    vpaList: root.getVpaList(),
    netAmount: root.transaction.amount.payableAmount,
    returnUrl: root.transaction.getMerchantRedirectingUrl(),
    validateVpaApi: root.upiValidateVpaApi,
    setMessage: root.message.setMessage,
    confirmPayment: root.confirmPaymentApi,
    offerApplied: root.isOfferApplied,
    validateOffer: root.offerValidateApi,
    applyOffer: root.offerTxnApplyApi,
    removeOffer: root.removeOffer,
    isSubscriptionEnabled: root.merchant.subscription.isEnabled,
    isUpiIntentAllowed: root.merchant.isUpiIntentAllowed,
    cancelIntent: root.cancelIntentApi,
    redirectUrl: root.transaction.initiatingEntity.returnUrl,
    intentId: root.transaction.id.intentId,
    deleteCardApi: root.deleteCardApi,
    getCards: root.getCards,
  }))

  const navigation = useNavigation()
  const [upiId, setUpiId] = useState('')

  const [selectedItem, setSelectedItem] = useState('')
  const [isProceedClicked, setIsProceedClicked] = useState(false)
  const [actionMenuInfo, setActionMenuInfo] = useState({ visible: false, layoutEvent: null, item: null } as any)
  const [confirmAlertInfo, setConfirmAlertInfo] = useState(false)
  const [showToastMessage, setShowToastMessage] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const [vpaInfo, setVpaInfo] = useState('')
  const [lazyValidateVpaApi, { state: validateVpaState, data: validateVpaData }] = useLazyApiData(validateVpaApi)
  const [confirmPaymentApi, { state: confirmPaymentState, data: confirmPaymentData }] = useLazyApiData(confirmPayment)
  const [validateOfferApi, { state: validateOfferState }] = useLazyApiData(validateOffer)
  const [lazyApplyOffer, { state: applyOfferState, data: applyOfferData }] = useLazyApiData(applyOffer)
  const [cancelIntentApi, { state }] = useLazyApiData(cancelIntent)

  useEffect(() => {
    if (state === 'fulfilled' || state === 'rejected') {
      if (isIFrame()) {
        window.parent.postMessage(
          {
            txnId: 0,
            intentId,
            status: 'bounced',
            message: 'Retry attempts exceeded',
          },
          '*',
        )
      } else {
        sendErrorIfNotIframe('bounced', 'Retry attempts exceeded', redirectUrl, intentId)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])
  const [lazyDeleteCardApi, { state: deleteCardState, data: deleteCardData }] = useLazyApiData(deleteCardApi)

  const isOfferApplied = offerApplied(PAYMENT_MODE_SUBTYPES.UPI)

  const { navigate } = useNavigation()
  const confirmUPIPayment = useCallback(() => {
    if (isSubscriptionEnabled) {
      navigate(pages.review, {
        paymentMode: 'UPI',
        paymentModeTitle: 'UPI',
      })
    } else {
      //call confirm api
      confirmPaymentApi()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, upiId])

  useEffect(() => {
    setActionMenuInfo({ ...actionMenuInfo, visible: false })
    setConfirmAlertInfo(false)
    //TODO: show custom toast
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (validateVpaState === 'fulfilled') {
      if (isOfferApplied) {
        validateOfferApi({ vpa: upiId }, { type: 110, subType: 566 })
      }
      // else if (isProceedClicked) {
      //   confirmUPIPayment()
      // }
    }
  }, [isOfferApplied, upiId, validateOfferApi, validateVpaState])

  useEffect(() => {
    if (validateOfferState === 'fulfilled') {
      setVpaInfo(staticStrings.offerApplied)
    } else {
      setVpaInfo(staticStrings.offerErrorUpi)
    }
  }, [validateOfferState])

  useEffect(() => {
    if (applyOfferState === 'fulfilled') {
      confirmUPIPayment()
    } else if (applyOfferState === 'rejected') {
      setMessage(applyOfferData?.failureMessage ?? 'Invalid offer', MessageTypes.error, '')
    }
  }, [applyOfferData, applyOfferState, confirmUPIPayment, setMessage])

  useEffect(() => {
    if (validateVpaState === 'rejected') {
      onModalDismiss()
      setMessage(validateVpaData?.message, MessageTypes.error, validateVpaData?.code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMessage, validateVpaState])

  useEffect(() => {
    if (deleteCardState === 'pending') {
      setIsDeleting(true)
    } else if (deleteCardState === 'fulfilled') {
      setIsDeleting(false)
      setConfirmAlertInfo(false)
      if (vpaList?.length < 2) {
        addNewTapped?.()
      }
      if (deleteCardData?.status === 'SUCCESS') {
        setShowToastMessage(true)
      }
    } else if (deleteCardState === 'rejected') {
      setIsDeleting(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteCardState])

  useEffect(() => {
    if (confirmPaymentState === 'fulfilled') {
      navigation.reset({
        index: 0,
        routes: [{ name: pages.progress }],
      })
    } else if (confirmPaymentState === 'rejected') {
      if (confirmPaymentData.message === MAX_RETRIES_EXCEEDED_ERROR) {
        cancelIntentApi()
      }
    }
  }, [confirmPaymentData, confirmPaymentState, navigation, returnUrl])

  const style = styles(theme)

  const addNewTapped = () => {
    setUpiId('')
    removeOffer()
    addNew()
  }

  const handleProceed = () => {
    if (isOfferApplied && validateOfferState === 'fulfilled') {
      setVpaInfo('')
      lazyApplyOffer()
    } else if (validateVpaState === 'fulfilled') {
      setVpaInfo('')
      confirmUPIPayment()
    } else {
      setIsProceedClicked(true)
      lazyValidateVpaApi(selectedItem, false)
    }
  }

  const renderItem = ({ item, index }: any) => {
    const isSelected = selectedItem === `${item?.key}`
    const itemClick = () => {
      if (item.title !== ADD_NEW_UPI && !isSelected) {
        setSelectedItem(`${item.key}`)
        setUpiId(item.title)
        setVpaInfo('')
        lazyValidateVpaApi(item.title, false)
      } else if (item.title === ADD_NEW_UPI) {
        addNewTapped()
      }
    }
    const style = styles(theme)

    return (
      <Box flexDirection="column">
        <Box pv={24} paddingVertical={0} flexDirection="row" alignItems="center">
          {item.title === ADD_NEW_UPI ? (
            <Box
              flexDirection={'row'}
              testID="add-new-upi-id"
              onPress={addNewTapped}
              underlayColor="LIGHTEST_GREY"
              ph={24}>
              <Box
                height={32}
                width={32}
                borderRadius={32}
                border={1}
                borderColor={theme.brandColor as ColorOptions}
                justifyContent="center"
                borderStyle="dashed"
                backgroundColor={convertHexToRGBA(theme.brandColor, 10) as ColorOptions}
                alignItems="center">
                <Text {...style.addNew}>+</Text>
              </Box>
              <Spacer direction="horizontal" size={16} />
              <Box justifyContent="center">
                <Text {...style.addNew}>{item?.title}</Text>
              </Box>
            </Box>
          ) : (
            <Box f={1} flexDirection="row" ph={24} alignItems="center" justifyContent={'space-between'}>
              <Box
                testID={`vpa-${item?.title}`}
                onPress={itemClick}
                underlayColor={convertHexToRGBA(theme?.brandColor, 10) as ColorOptions}
                flexDirection="row">
                <Image height={28} width={28} resizeMode="contain" source={item?.imageUrl} />
                <Spacer direction="horizontal" size={8} />
                <Box justifyContent="center" flexDirection="column">
                  <Text
                    {...style.itemText}
                    textAlign="left"
                    numberOfLines={2}
                    color={isSelected ? (theme?.brandColor as ColorOptions) : (theme?.bodyTextColor as ColorOptions)}>
                    {item?.title}
                  </Text>

                  {isSelected && (
                    <>
                      <Spacer direction="vertical" size={8} />
                      <Text
                        fontFamily="jioType-medium"
                        fontSize={12}
                        color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}>
                        {vpaInfo}
                      </Text>
                    </>
                  )}
                </Box>
              </Box>
              {/* {isSelected && (
                <Box justifyContent={'center'}>
                  <Icon id="ROUND_TICK_FILLED" color={theme?.brandColor as ColorOptions} size={16} />
                </Box>
              )} */}
              <Box
                alignSelf={'center'}
                alignItems="center"
                justifyContent={'center'}
                underlayColor={'ALTO_DISABLED'}
                testID={`view-more-${index}`}
                onPress={(e: any) => {
                  const event = e?.nativeEvent
                  const customValue = {
                    top: event?.clientY - event?.offsetY,
                    left: event?.clientX - event?.offsetX,
                    height: actionMenuInfo?.layoutEvent?.height,
                    width: actionMenuInfo?.layoutEvent?.width,
                  }
                  setActionMenuInfo({
                    ...actionMenuInfo,
                    visible: !actionMenuInfo.visible,
                    layoutEvent: customValue,
                    item: item,
                  })
                }}
                onLayout={({ nativeEvent }) => {
                  setActionMenuInfo({ ...actionMenuInfo, layoutEvent: nativeEvent.layout })
                }}>
                <IconNew
                  id="MENU"
                  size={20}
                  color={isSelected ? ('PRIMARY' as ColorOptions) : ('SECONDARY_GREY' as ColorOptions)}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box {...style.separatorStyle} />
      </Box>
    )
  }

  const _toggleActionMenu = () => {
    setActionMenuInfo({ ...actionMenuInfo, visible: !actionMenuInfo.visible })
  }

  const renderActionMenu = () => {
    return (
      <Box {...deleteStyles.container} testID={'actionMenuModal'}>
        <Box
          top={actionMenuInfo?.layoutEvent?.top + actionMenuInfo?.layoutEvent?.height / 2 + 2}
          right={
            Dimensions.get('window').width - actionMenuInfo?.layoutEvent?.left - actionMenuInfo?.layoutEvent?.width / 2
          }
          {...deleteStyles.shadowStyle}>
          <Box
            {...deleteStyles.listItem}
            testID="delete-vpa"
            onPress={() => {
              onItemPress(actionMenuInfo?.item)
              _toggleActionMenu()
            }}>
            <Text fontSize={12} fontFamily="jioType-medium" color={'TEXT_ERROR' as ColorOptions}>
              Delete VPA
            </Text>
          </Box>
        </Box>
      </Box>
    )
  }

  const onItemPress = (item: any) => {
    //alert('delete card')
    //console.log('delete', JSON.stringify(item))
    setUpiId(item.title)
    setConfirmAlertInfo(true)
  }
  const isLoading = validateVpaState === 'pending' || confirmPaymentState === 'pending'

  return (
    <Box flex={1} marginTop={32} paddingBottom={24} marginHorizontal={0}>
      <ModBox show={actionMenuInfo.visible} onPress={_toggleActionMenu}>
        {ReactDOM.createPortal(renderActionMenu(), document.body)}
      </ModBox>
      <Box flexDirection={'row'} justifyContent="space-between" marginHorizontal={24}>
        <Text {...style.savedVPA}>{`Saved UPI ID (${vpaList.length - 1})`}</Text>
        {isUpiIntentAllowed && !isDirectUPI && (
          <Box
            onPress={onModalDismiss}
            alignSelf="center"
            underlayColor={convertHexToRGBA(theme.bodyTextColor, 10) as ColorOptions}>
            <Icon id={'CLOSE'} size={16} color={convertHexToRGBA(theme.bodyTextColor, 70) as ColorOptions} />
          </Box>
        )}
      </Box>
      <Spacer size={24} />

      <FlatList data={vpaList ?? []} renderItem={renderItem} />

      <Box {...style.payBtnMobile} zIndex={98} marginHorizontal={24}>
        <PaySecurelyButton
          disabled={!upiId}
          amount={netAmount}
          testID={'upiPaySecurelyBtn'}
          onPress={handleProceed}
          isLoading={isLoading}
        />
      </Box>
      <ModalConfirmDelete
        data={actionMenuInfo?.item}
        theme={theme}
        title={'Delete Saved VPA'}
        show={confirmAlertInfo}
        close={() => {
          setConfirmAlertInfo(false)
        }}
        onProceed={() => {
          lazyDeleteCardApi(actionMenuInfo?.item?.instrumentId, true)
        }}
        isLoading={isDeleting}
        isCard={false}
      />
      <ToastMessage
        show={showToastMessage}
        onCancel={() => setShowToastMessage(false)}
        cardNumber={upiId ?? 'NA'}
        isCard={false}
      />
    </Box>
  )
}

const AddNew = ({ onModalDismiss, isDirectUPI }: { onModalDismiss: () => void; isDirectUPI: boolean }) => {
  const {
    theme,
    netAmount,
    setMessage,
    returnUrl,
    vlaidateVpa,
    confirmPayment,
    offerApplied,
    validateOffer,
    applyOffer,
    isSubscriptionAllowedVPA,
    isSubscriptionEnabled,
    addPaymentMethod,
    updateIsOfferApplied,
    isUpiIntentAllowed,
  } = useStores((root) => ({
    theme: root.config.merchantTheme,
    netAmount: root.transaction.amount.payableAmount,
    setMessage: root.message.setMessage,
    returnUrl: root.transaction.getMerchantRedirectingUrl(),
    vlaidateVpa: root.upiValidateVpaApi,
    confirmPayment: root.confirmPaymentApi,
    offerApplied: root.isOfferApplied,
    validateOffer: root.offerValidateApi,
    applyOffer: root.offerTxnApplyApi,
    isSubscriptionAllowedVPA: root.isSubscriptionAllowedVPA,
    isSubscriptionEnabled: root.merchant.subscription.isEnabled,
    addPaymentMethod: root.transaction.addPaymentMethod,
    updateIsOfferApplied: root.transaction.updateIsOfferApplied,
    isUpiIntentAllowed: root.merchant.isUpiIntentAllowed,
  }))

  const [validatedVpaId, setValidatedVpaId] = useState('')
  const [isOfferValidated, setIsOfferValidated] = useState(false)
  const [upiId, setUpiId] = useState('')
  const [vpaTouched, setVpaTouched] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [VPAError, setVPAError] = useState('')
  const [toolTipVisibility, setToolTipVisibility] = useState(false)
  const [tooltipLeft, setTooltipLeft] = useState(-1)
  const [tooltipTop, setTooltipTop] = useState(-1)
  const [isUPIChecked, setIsUPIChecked] = useState(true)

  const navigation = useNavigation()

  const [lazyValidateVpaApi, { state: validateVpaState, data: validateVpaData }] = useLazyApiData(vlaidateVpa)
  const [confirmPaymentApi, { state: confirmPaymentState, data: confirmPaymentData }] = useLazyApiData(confirmPayment)
  const [validateOfferApi, { state: validateOfferState }] = useLazyApiData(validateOffer)
  const [lazyApplyOffer, { state: applyOfferState, data: applyOfferData }] = useLazyApiData(applyOffer)

  const isOfferApplied = offerApplied(PAYMENT_MODE_SUBTYPES.UPI)

  const confirmUPIPayment = useCallback(() => {
    if (isSubscriptionEnabled) {
      navigation.navigate(pages.review, {
        paymentMode: 'UPI',
        paymentModeTitle: 'UPI',
      })
    } else {
      //call confirm api
      confirmPaymentApi()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation, upiId])

  useEffect(() => {
    if (validateVpaState === 'fulfilled') {
      setValidatedVpaId(
        validateVpaData?.payer?.vpa ?? validateVpaData?.transaction?.metadata?.payeeVpaDetails?.virtualAddress ?? upiId,
      )
      if (validatedVpaId) {
        if (isOfferApplied && validateOfferState !== 'rejected') {
          validateOfferApi({ vpa: upiId }, { type: 110, subType: 566 })
        } else {
          updateIsOfferApplied(false)
          //confirmUPIPayment()
        }
      } else if (isOfferApplied && validateOfferState !== 'rejected') {
        validateOfferApi({ vpa: upiId }, { type: 110, subType: 566 })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateVpaState])

  useEffect(() => {
    if (validateOfferState === 'fulfilled') {
      if (validatedVpaId) {
        lazyApplyOffer()
      }
      setIsOfferValidated(true)
      setVPAError(staticStrings.offerApplied)
    } else if (validateOfferState === 'rejected') {
      setVPAError(staticStrings.offerErrorUpi)
      setIsOfferValidated(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyApplyOffer, validateOfferState])

  useEffect(() => {
    if (applyOfferState === 'fulfilled') {
      confirmUPIPayment()
    } else if (applyOfferState === 'rejected') {
      setVPAError(applyOfferData?.failureMessage ?? 'Invalid offer')
    }
  }, [applyOfferState, applyOfferData, confirmUPIPayment, upiId])

  useEffect(() => {
    if (validateVpaState === 'rejected') {
      setVPAError(validateVpaData?.message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setMessage, validateVpaState])

  useEffect(() => {
    if (confirmPaymentState === 'fulfilled') {
      navigation.reset({
        index: 0,
        routes: [{ name: pages.progress }],
      })
    } else if (confirmPaymentState === 'rejected') {
      const response = confirmPaymentData
      navigation.navigate(pages.ErrorView, { title: 'Error occurred', error: response?.message, rUrl: returnUrl })
    }
  }, [confirmPaymentData, confirmPaymentState, navigation, returnUrl])

  const validateVPAAPI = (upiId: string) => {
    //setValidatedVpaId(upiId)
    lazyValidateVpaApi(upiId)
  }

  const handleProceed = async () => {
    analyticsEvent('pay_button_pressed', { mode: 'upi_collect', amount: netAmount })
    if (validatedVpaId && validateVpaState === 'fulfilled') {
      const upi = {
        id: 0,
        vpa: validatedVpaId,
        paymentType: 'UPI',
        isSave: isUPIChecked,
      } as UPIType
      addPaymentMethod(upi)
      if (isOfferApplied) {
        lazyApplyOffer()
      } else {
        updateIsOfferApplied(false)
        confirmUPIPayment()
      }
    } else {
      lazyValidateVpaApi(upiId)
    }
  }

  const style = styles(theme)

  const searchFn = function (item: string) {
    const enteredVpaText = upiId.split('@')
    const search = enteredVpaText.length > 1 ? enteredVpaText[1].toLowerCase() : ''
    return item.substring(1).toLowerCase().startsWith(search)
  }

  const getTagData = () => {
    if (upiId.length > 3 && upiId.includes('@')) {
      const enteredVpaText = upiId.split('@')[1]
      const vpaList = enteredVpaText ? allVpa(isSubscriptionEnabled) : popularVpa(isSubscriptionEnabled)
      const vpaSearchList = vpaList.filter(searchFn)
      return vpaSearchList
    } else {
      return []
    }
  }

  const validateUPIInput = (text: string, isSelected: boolean) => {
    let schema = Yup.object().shape({
      name: vpaOrNumber,
    })
    schema
      .isValid({
        name: text,
      })
      .then((valid: any) => {
        if (valid) {
          if (isSubscriptionEnabled) {
            if (isSubscriptionAllowedVPA(text)) {
              setIsValid(true)
              setVPAError('')
              isSelected && validateVPAAPI(text)
            } else {
              setIsValid(false)
              setVPAError('Invalid VPA for subscription')
            }
          } else {
            setIsValid(true)
            text !== upiId && setValidatedVpaId('')
            setVPAError('')
            isSelected && validateVPAAPI(text)
          }
        } else {
          setIsValid(false)
          text !== upiId && setValidatedVpaId('')
          setVPAError('Invalid VPA')
        }
      })
      .catch((err: any) => {
        setVPAError(err.errors)
      })
  }

  const validateVpaOrNumber = () => {
    if (isValid) {
      validateVPAAPI(upiId)
    }
  }

  const isLoading = confirmPaymentState === 'pending'

  const getImages = useCallback(() => {
    return [
      require('../../assets/images/ic_gpay.png'),
      require('../../assets/images/ic_paytm.png'),
      require('../../assets/images/ic_phonepe.png'),
      require('../../assets/images/ic_bheamupi.png'),
    ]
  }, [])

  const handleInfoIconPress = (evt: any) => {
    setTooltipLeft(evt?.nativeEvent?.pageX)
    setTooltipTop(evt?.nativeEvent?.pageY)
    setToolTipVisibility(true)
  }
  return (
    <Box maxHeight={'90vh'} overflowY="scroll">
      <Box {...style.containerView}>
        <KeyboardAvoidingView behavior={'padding'} style={style.keyboardView}>
          {isUpiIntentAllowed && !isDirectUPI && (
            <>
              <Box flexDirection={'row'} justifyContent="space-between">
                <Text {...style.savedVPA} testID="add-new-upi-id">
                  Add New UPI ID
                </Text>
                <Box
                  onPress={onModalDismiss}
                  alignSelf="center"
                  underlayColor={convertHexToRGBA(theme.bodyTextColor, 10) as ColorOptions}>
                  <Icon id={'CLOSE'} size={16} color={convertHexToRGBA(theme.bodyTextColor, 70) as ColorOptions} />
                </Box>
              </Box>
              <Spacer size={24} />
            </>
          )}

          <Box {...style.textContainer}>
            <Box marginBottom={16}>
              <Text {...style.payText}>Pay by any UPI app</Text>
            </Box>
            <MaterialTextInput
              testID={'upiVpaTextInput'}
              label="UPI ID / UPI Number"
              editable={true}
              value={upiId}
              touched={vpaTouched}
              error={VPAError}
              errorColor={
                isOfferValidated && VPAError === staticStrings.offerApplied
                  ? (theme.brandColor as ColorOptions)
                  : undefined
              }
              placeholderTextColor={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
              onFocus={() => {
                setVpaTouched(false)
              }}
              onBlur={() => {
                validateUPIInput(upiId, false)
                setTimeout(() => {
                  setVpaTouched(true)
                }, 500)
              }}
              onChangeText={(text: string) => {
                validateUPIInput(text, false)
                setUpiId(text)
              }}
              color={theme?.bodyTextColor as ColorOptions}
              bottomLineColor={theme?.brandColor as ColorOptions}
              tintColor={theme?.brandColor as ColorOptions}
            />
            <ModBox show={upiId.length > 1}>
              {validatedVpaId && validatedVpaId.length > 0 && validateVpaState === 'fulfilled' ? (
                <Box flexDirection={'row'} justifyContent={'flex-start'} alignItems={'center'}>
                  <VerifiedIcon width={18} height={15} color={'#00C113'} />
                  <Spacer size={7} direction={'horizontal'} />
                  <TextNew color={'#1CB700' as ColorOptions} type={'bodyMedium12'}>
                    {validateVpaData?.transaction?.metadata?.payeeVpaDetails?.name
                      ? `Verified as ${validateVpaData?.transaction?.metadata?.payeeVpaDetails?.name}`
                      : 'Verified'}
                  </TextNew>
                </Box>
              ) : (
                <Box
                  underlayColor={'ALTO_DISABLED'}
                  onPress={validateVpaOrNumber}
                  flexDirection={'row'}
                  alignItems="center">
                  <TextNew color={theme.brandColor as ColorOptions} type={'bodyMedium12'}>
                    Verify to proceed
                  </TextNew>
                  {validateVpaState === 'pending' && <Spacer size={4} direction={'horizontal'} />}
                  {validateVpaState === 'pending' && <Loader type={'small'} color={theme.brandColor as ColorOptions} />}
                </Box>
              )}
              <Spacer size={10} direction="vertical" />
            </ModBox>
          </Box>
          {getTagData().length > 0 && (
            <Box>
              <Text
                value="Select your UPI Provider"
                fontFamily="jioType-medium"
                fontSize={12}
                color={convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions}
              />
              <Spacer direction="vertical" size={16} />
              <Box {...style.tagsViewContainer}>
                {getTagData().map((item: any) => (
                  <BankNameItem
                    theme={theme}
                    item={item}
                    onPress={() => {
                      const enteredVpaText = upiId.split('@')
                      const vpaText = enteredVpaText.length > 0 ? `${enteredVpaText[0]}${item}` : item
                      setUpiId(vpaText)
                      validateUPIInput(vpaText, false)
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Box pv={0}>
            <Box pv={'SMALL'} direction={'row'} align={'center'}>
              <CheckBox
                onChange={() => setIsUPIChecked(!isUPIChecked)}
                checked={isUPIChecked}
                color={theme?.brandColor as ColorOptions}
              />
              <Spacer direction={'horizontal'} size={'SMALL'} />
              <Text {...style.secureSave}>{'Securely Save VPA'}</Text>
              <Spacer direction={'horizontal'} size={'SMALL'} />
              <Box
                underlayColor={'TRANSPARENT'}
                onPress={handleInfoIconPress}
                onHover={(value: boolean) => {
                  setToolTipVisibility(value)
                }}>
                <Icon id={'INFO'} color={theme?.brandColor as ColorOptions} size={'LARGE'} />
              </Box>
            </Box>
            <UPIToolTip visible={toolTipVisibility} tooltipLeft={tooltipLeft} tooltipTop={tooltipTop} />
          </Box>
        </KeyboardAvoidingView>
        <Spacer size={56} />

        <PaySecurelyButton
          disabled={validatedVpaId === ''}
          amount={netAmount}
          testID={'upiPaySecurelyBtn'}
          onPress={() => handleProceed()}
          isLoading={isLoading}
        />
        <Box {...style.payBtnMobile}>
          {/* <Box paddingTop={32} flexDirection="row">
            <Box {...style.paySeparatorStyle} />
            <Box width="20%">
              <Text {...style.payText}>Pay Using Any UPI App</Text>
            </Box>
            <Box {...style.paySeparatorStyle} />
          </Box> */}

          {/* <Box paddingTop={24} justifyContent="center" alignItems="center" flexDirection="row">
            {getImages()?.map((imageSrc: any) => {
              return (
                <Box paddingHorizontal={12}>
                  <Image {...style.botttomIcon} resizeMode="contain" source={imageSrc} />
                </Box>
              )
            })}
          </Box> */}
        </Box>
      </Box>
    </Box>
  )
}

const styles = (theme: any) => ({
  containerView: { paddingBottom: 24, paddingHorizontal: 24 } as BoxProps,
  textContainer: { marginBottom: 0 } as BoxProps,
  tagText: {
    color: theme?.brandColor as ColorOptions,
    textAlign: 'left',
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'jioType-medium',
  } as TextProps,
  secureSave: {
    fontSize: 14,
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
    fontWeight: '700',
  } as TextProps,
  item: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingVertical: 8,
    marginRight: 0,
    margin: 4,
    borderRadius: 8,
    borderColor: theme?.brandColor as ColorOptions,
    borderWidth: 1,
    backgroundColor: convertHexToRGBA(theme?.brandColor, 10) as ColorOptions,
  } as BoxProps,
  tagsViewContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingBottom: 16,
  } as BoxProps,
  payBtn: { marginTop: 24, bottom: 0, alignSelf: 'flex-end', right: 0 } as BoxProps,
  payBtnMobile: { marginTop: 24, justifyContent: 'flex-end' } as BoxProps,
  separatorStyle: {
    height: 1,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 10) as ColorOptions,
  } as BoxProps,
  payText: {
    fontSize: 12,
    fontFamily: 'jioType-medium',
    color: convertHexToRGBA(theme?.bodyTextColor, 50) as ColorOptions,
  } as TextProps,
  savedVPA: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'jioType-medium',
    color: theme.bodyTextColor,
  } as TextProps,
  addNew: {
    textAlign: 'center',
    color: theme?.brandColor as ColorOptions,
    fontSize: 14,
    fontFamily: 'jioType-medium',
  } as TextProps,
  logoContainer: {
    height: 32,
    width: 32,
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  } as BoxProps,
  itemText: {
    color: theme.bodyTextColor,
    fontSize: 14,
    fontFamily: 'jioType-medium',
    textAlign: 'center',
  } as TextProps,
  paySeparatorStyle: {
    width: '40%',
    alignSelf: 'center',
    height: 1,
    backgroundColor: convertHexToRGBA(theme?.bodyTextColor, 10) as ColorOptions,
  } as BoxProps,
  botttomIcon: {
    width: 32,
    height: 32,
  },
  contentContainer: {
    height: (height - 40) / 1.4,
  },
  keyboardView: {
    flex: 1,
    paddingTop: 32,
  },
  deleteLayout: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
  } as BoxProps,
  deleteIcon: {
    width: 23,
    height: 23,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-end',
  } as BoxProps,
})

const deleteStyles = {
  container: {
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    position: 'absolute',
  } as BoxProps,
  shadowStyle: {
    position: 'absolute',
    shadowOffset: { width: 1, height: 1 },
    shadowRadius: 2,
    shadowColor: 'GREY_MATTE',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: convertHexToRGBA('#DE0000', 15) as ColorOptions,
    zIndex: 5,
    backgroundColor: 'WHITE',
    flexDirection: 'column',
    underlayColor: 'WHITE',
    //width: 104,
  } as BoxProps,
  listItem: {
    pv: 10,
    ph: 20,
    underlayColor: 'TRANSPARENT',
  } as BoxProps,
}

export { UPIInputModal }
